/**
 * RetrievalProgressRefreshManager
 *
 * Headless component (renders null) that owns the SSE subscription for
 * FULL_ORG_REFRESH and METADATA_TYPE retrievals.
 *
 * Mount once in AppContent BEFORE <Router> so it is never unmounted during
 * route navigation (Scenario 3).
 *
 * Responsibilities:
 * 1. Calls useRetrievalProgressRefresh() to hold the SSE connection.
 * 2. Registers connect/disconnect fns in retrievalProgressStore on mount so
 *    any component (e.g. FileExplorer) can trigger the connection (Scenario 1).
 * 3. Syncs all SSE state to retrievalProgressStore so consumers can read it.
 * 4. Mirrors state into activityStore so ActivityDropdown shows live progress.
 * 5. Auto-connects when selectedOrg.latestMetadataRetrieval shows IN_PROGRESS
 *    on app load / page refresh (Scenario 2).
 */

import { useEffect, useRef } from 'react';
import { useShallow } from 'zustand/react/shallow';
import { useQueryClient } from '@tanstack/react-query';
import { useRetrievalProgressRefresh } from '@/hooks/metadata_v1/useRetrievalProgressRefresh';
import { useRetrievalProgressStore } from '@/stores/retrievalProgressStore';
import { subscribeToOrgChange } from '@/lib/org-scope';
import { useActivityStore, type ActivityType } from '@/stores/activityStore';
import { useAuthStoreV1 } from '@/stores/authStore_v1';
import { useSecurityScanStore } from '@/stores/securityScanStore';
import { useIngestJobStore } from '@/stores/ingestJobStore';
import { showRetrievalCompleteToast } from '@/constants/workflow/RetrievalCompleteToast';
import {
  RETRIEVAL_TOAST_FULL_REFRESH,
  retrievalToastMetadataType,
} from '@/constants/workflow/retrievalToasts';
import { clearFrontendSessionAndRedirectToLogin } from '@/dataProvider/axios';

function getActivityType(
  retrievalType: 'full_org_refresh' | 'metadata_type' | null
): ActivityType {
  return retrievalType === 'metadata_type' ? 'metadata_sync_type' : 'metadata_sync_full';
}

export function RetrievalProgressRefreshManager() {
  const {
    status,
    message,
    progressPercentage,
    currentPhase,
    totalItems,
    processedItems,
    retrievalType,
    phaseDetails,
    isConnected,
    isComplete,
    connectionError,
    isUnauthenticated,
    connect,
    disconnect,
  } = useRetrievalProgressRefresh();

  const queryClient = useQueryClient();
  const {
    setConnectFn,
    setDisconnectFn,
    setProgress,
    setLastRefreshCompletedAt,
    // Auto-reconnects below go through the store rather than calling the hook's
    // `connect` directly, so the store records which organization owns the stream
    // and can bar the other one's frames.
    connect: connectStream,
  } = useRetrievalProgressStore();
  const { startActivity, updateActivity, completeActivity, failActivity, removeActivity } = useActivityStore(
    useShallow((s) => ({
      startActivity: s.startActivity,
      updateActivity: s.updateActivity,
      completeActivity: s.completeActivity,
      failActivity: s.failActivity,
      removeActivity: s.removeActivity,
    }))
  );
  const selectedOrg = useAuthStoreV1((state) => state.selectedOrg);
  const user = useAuthStoreV1((state) => state.user);
  const checkOngoingJobAndRehydrate = useSecurityScanStore((s) => s.checkOngoingJobAndRehydrate);
  const checkIngestJobAndRehydrate = useIngestJobStore((s) => s.checkOngoingJobAndRehydrate);

  // Tracks which activity entry in activityStore we are currently updating.
  // Set when the SSE connection opens, cleared when complete/failed.
  const activeActivityIdRef = useRef<string | null>(null);
  // Captures connectMetadataType at connect time (before the store clears it) for use in the completion toast.
  const activeMetadataTypeRef = useRef<string | null>(null);

  // -------------------------------------------------------------------------
  // 1. Register connect/disconnect in global store
  // Re-registers whenever the callbacks change (stable via useCallback in hook).
  // -------------------------------------------------------------------------
  useEffect(() => {
    setConnectFn(connect);
  }, [connect, setConnectFn]);

  useEffect(() => {
    setDisconnectFn(disconnect);
  }, [disconnect, setDisconnectFn]);

  // -------------------------------------------------------------------------
  // 2. Sync SSE state → retrievalProgressStore
  // -------------------------------------------------------------------------
  useEffect(() => {
    setProgress({
      status,
      message,
      progressPercentage,
      currentPhase,
      totalItems,
      processedItems,
      retrievalType,
      phaseDetails: phaseDetails ?? null,
      isConnected,
      isComplete,
      connectionError,
    });
  }, [
    status,
    message,
    progressPercentage,
    currentPhase,
    totalItems,
    processedItems,
    retrievalType,
    phaseDetails,
    isConnected,
    isComplete,
    connectionError,
    setProgress,
  ]);

  // -------------------------------------------------------------------------
  // Poll GetMetadataTypes every 1 minute while stream is live
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isConnected || isComplete) return;

    // Refetch immediately on connect so short-lived retrievals (<30s) still get a refresh
    queryClient.refetchQueries({ queryKey: ['metadataTypes'] });
    const intervalId = setInterval(() => {
      queryClient.refetchQueries({ queryKey: ['metadataTypes'] });
    }, 30_000);

    return () => clearInterval(intervalId);
  }, [isConnected, isComplete, queryClient]);

  // -------------------------------------------------------------------------
  // 3a. Mirror SSE → activityStore: start when connection opens
  // The retrievalId is pre-set in the store by connect() before the hook opens
  // the SSE connection, so it is available here synchronously.
  // -------------------------------------------------------------------------
  const { clearConnectMetadataType } = useRetrievalProgressStore();
  useEffect(() => {
    if (!isConnected) return;

    const state = useRetrievalProgressStore.getState();
    const retrievalId = state.retrievalId;
    if (!retrievalId) return;
    // Already tracked — an auto-reconnect (4a / 4b / 5) registered this activity
    // before opening the stream, and restarting it here would reset its progress
    // and its start time.
    if (activeActivityIdRef.current === retrievalId) {
      clearConnectMetadataType();
      return;
    }

    activeActivityIdRef.current = retrievalId;
    // Prefer connectMetadataType to decide type (set by FileExplorer for METADATA_TYPE refresh);
    // retrievalType from SSE may still be null when connection just opened.
    const type = state.connectMetadataType
      ? 'metadata_sync_type'
      : getActivityType(retrievalType);
    const metadata =
      type === 'metadata_sync_type' && state.connectMetadataType
        ? { metadataType: state.connectMetadataType }
        : undefined;
    activeMetadataTypeRef.current = state.connectMetadataType;
    startActivity(retrievalId, type, message ?? 'Starting...', metadata);
    clearConnectMetadataType();
    // Only trigger when the connection opens (isConnected flips to true)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConnected]);

  // -------------------------------------------------------------------------
  // 3b. Mirror SSE → activityStore: update progress on each event
  // -------------------------------------------------------------------------
  useEffect(() => {
    const id = activeActivityIdRef.current;
    if (!id) return;

    if (isComplete) {
      completeActivity(id, message ?? 'Completed');
      setTimeout(() => removeActivity(id), 3_000);
      activeActivityIdRef.current = null;
      queryClient.invalidateQueries({ queryKey: ['user-data'] });
      queryClient.invalidateQueries({ queryKey: ['metadataTypes'] });
      // Force refetch so observers get fresh data; invalidation alone doesn't refetch if no observer was mounted
      queryClient.refetchQueries({ queryKey: ['retrievalMetadataItems'] });
      setLastRefreshCompletedAt(Date.now());

      const metadataType = activeMetadataTypeRef.current;
      activeMetadataTypeRef.current = null;
      showRetrievalCompleteToast(
        metadataType
          ? retrievalToastMetadataType(metadataType)
          : RETRIEVAL_TOAST_FULL_REFRESH
      );

      // Backend may have triggered a security scan or ingest after retrieval; connect to SSE if there's an ongoing job
      const orgId = selectedOrg?.id;
      const userId = user?.id;
      if (orgId != null && userId != null) {
        void checkOngoingJobAndRehydrate(String(orgId), String(userId));
        void checkIngestJobAndRehydrate(String(orgId), String(userId));
      }
      return;
    }

    if (status === 'in_progress') {
      updateActivity(id, {
        progress: progressPercentage,
        message: message ?? undefined,
      });
    }
  }, [
    progressPercentage,
    message,
    status,
    isComplete,
    updateActivity,
    completeActivity,
    queryClient,
    setLastRefreshCompletedAt,
    selectedOrg?.id,
    user?.id,
    checkOngoingJobAndRehydrate,
    checkIngestJobAndRehydrate,
  ]); // queryClient kept — still used for refetchQueries / invalidateQueries in this effect

  // -------------------------------------------------------------------------
  // 3c. Mirror SSE → activityStore: handle connection errors
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!connectionError) return;
    const id = activeActivityIdRef.current;
    if (!id) return;
    failActivity(id, connectionError);
    activeActivityIdRef.current = null;
  }, [connectionError, failActivity]);

  // -------------------------------------------------------------------------
  // 3d. Unauthenticated — session expired mid-stream, redirect to login
  // -------------------------------------------------------------------------
  useEffect(() => {
    if (!isUnauthenticated) return;
    const id = activeActivityIdRef.current;
    if (id) {
      failActivity(id, 'Session expired');
      activeActivityIdRef.current = null;
    }
    clearFrontendSessionAndRedirectToLogin();
  }, [isUnauthenticated, failActivity]);

  // -------------------------------------------------------------------------
  // 4. Scenario 2 — auto-connect on page load / refresh
  // After useFetchUserData resolves and selectedOrg is populated, check if
  // there is an in-progress refresh retrieval and reconnect to SSE.
  // -------------------------------------------------------------------------
  const autoConnectedIdRef = useRef<string | null>(null);

  useEffect(() => {
    const retrieval = selectedOrg?.latestMetadataRetrieval;
    if (!retrieval) return;
    if (isConnected) return;

    const isRefreshType =
      retrieval.retrievalType === 'FULL_ORG_REFRESH' ||
      retrieval.retrievalType === 'METADATA_TYPE';

    if (retrieval.status === 'IN_PROGRESS' && isRefreshType) {
      // Guard: avoid re-connecting to the same retrieval that already completed
      if (autoConnectedIdRef.current === retrieval.id) return;
      autoConnectedIdRef.current = retrieval.id;

      // Pre-register the activity before calling connect so 3a picks it up
      activeActivityIdRef.current = retrieval.id;
      const type = getActivityType(
        retrieval.retrievalType === 'METADATA_TYPE' ? 'metadata_type' : 'full_org_refresh'
      );
      startActivity(retrieval.id, type, 'Reconnecting...');

      connectStream(retrieval.id);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    selectedOrg?.latestMetadataRetrieval?.id,
    selectedOrg?.latestMetadataRetrieval?.status,
    isConnected,
  ]);

  // -------------------------------------------------------------------------
  // 4b. Scenario 2 (full org refresh) — auto-connect on page load / refresh
  // A full org refresh does NOT flip the reused MetadataRetrieval row to
  // IN_PROGRESS (it stays the completed dataset anchor), so effect 4a above
  // can't detect it. The MetadataRefreshOperation row does track refresh
  // status and exposes metadataRetrievalId (the SSE stream key); it arrives on
  // selectedOrg via the same GetUserData query as latestMetadataRetrieval, so
  // reconnect here if a refresh is still queued/in-progress.
  // -------------------------------------------------------------------------
  const refreshOperation = selectedOrg?.latestRefreshOperation;

  useEffect(() => {
    if (!refreshOperation) return;
    if (isConnected) return;
    if (refreshOperation.status !== 'QUEUED' && refreshOperation.status !== 'IN_PROGRESS') return;

    const retrievalId = refreshOperation.metadataRetrievalId;
    // Don't double-connect: 4a may have already reconnected this retrieval,
    // or a previous run of this effect did.
    if (autoConnectedIdRef.current === retrievalId) return;

    autoConnectedIdRef.current = retrievalId;

    // Pre-register the activity before calling connect so 3a picks it up
    activeActivityIdRef.current = retrievalId;
    startActivity(retrievalId, getActivityType('full_org_refresh'), 'Reconnecting...');

    connectStream(retrievalId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [refreshOperation?.metadataRetrievalId, refreshOperation?.status, isConnected]);

  // -------------------------------------------------------------------------
  // 5. Organization switch — resume the refresh belonging to the organization we
  //    are entering.
  //
  // By the time this runs, `retrievalProgressStore` has already aborted the
  // stream and parked the refresh under the organization we are leaving (its
  // org-scope handler fans out synchronously), because the transport carries one
  // organization's refresh at a time. What is left is picking that refresh back
  // up on the way in.
  //
  // Resuming rather than leaving it to 4a/4b is the point: those effects read
  // `selectedOrg`, whose retrieval status is a snapshot from the last
  // GetUserData and still reports the state from BEFORE the sync was started.
  // Returning to the organization would therefore show nothing at all. The store
  // parks the retrieval it was streaming, which is exact.
  //
  // The activity is deliberately left in place while away: it lives in that
  // organization's partition, so it is invisible from here, and it starts
  // ticking again the moment the stream is back.
  // -------------------------------------------------------------------------
  useEffect(
    () =>
      subscribeToOrgChange((nextOrgId) => {
        // Cleared unconditionally: leaving `autoConnectedIdRef` set would make
        // 4a/4b refuse to attach to the organization we are entering.
        activeActivityIdRef.current = null;
        activeMetadataTypeRef.current = null;
        autoConnectedIdRef.current = null;

        const parked = useRetrievalProgressStore.getState().takeParkedRetrieval(nextOrgId);
        if (!parked) return;

        activeActivityIdRef.current = parked.retrievalId;
        activeMetadataTypeRef.current = parked.metadataType;
        autoConnectedIdRef.current = parked.retrievalId;

        // The activity is still in this organization's partition, holding the
        // progress it had when we left. `startActivity` keeps the progress and
        // start time of an entry that is already in progress, so this resumes it
        // rather than restarting it at 0%.
        startActivity(
          parked.retrievalId,
          parked.metadataType ? 'metadata_sync_type' : 'metadata_sync_full',
          'Reconnecting...',
          parked.metadataType ? { metadataType: parked.metadataType } : undefined
        );

        connectStream(
          parked.retrievalId,
          parked.metadataType ? { metadataType: parked.metadataType } : undefined
        );
        // Bridges the gap until the stream opens: without it the sync bar reads
        // the (stale) retrieval status on `selectedOrg` and shows idle for the
        // few hundred milliseconds the connection takes.
        setProgress({ status: 'in_progress', message: 'Reconnecting...' });
      }),
    [connectStream, setProgress, startActivity]
  );

  return null;
}
